<template>
  <div class="setting-category">
    <a-card :bordered="false" title="教材分类管理">
      <div class="toolbar">
        <a-radio-group v-model="bookTypeFilter" button-style="solid" @change="fetchList">
          <a-radio-button :value="1">数字教材</a-radio-button>
          <a-radio-button :value="2">电子书</a-radio-button>
        </a-radio-group>
        <CommonPermissions :p="$permissions.TEXTBOOK.CREATE">
          <a-button type="primary" icon="plus" @click="openCreateRoot">新建分类</a-button>
        </CommonPermissions>
      </div>

      <a-table
        row-key="id"
        :columns="columns"
        :data-source="treeData"
        :loading="loading"
        :pagination="false"
        :default-expand-all-rows="true"
      >
        <template slot="bookType" slot-scope="text">
          <a-tag :color="text === 1 ? 'blue' : 'cyan'">{{ bookTypeLabel(text) }}</a-tag>
        </template>
        <template slot="parentName" slot-scope="_, record">
          {{ parentDisplayName(record) }}
        </template>
        <template slot="action" slot-scope="_, record">
          <CommonPermissions :p="$permissions.TEXTBOOK.CREATE">
            <a @click="openAddChild(record)">添加分类</a>
          </CommonPermissions>
          <CommonPermissions :p="$permissions.TEXTBOOK.UPDATE">
            <a-divider type="vertical" />
            <a @click="openEdit(record)">编辑</a>
          </CommonPermissions>
          <CommonPermissions :p="$permissions.TEXTBOOK.DELETE">
            <a-divider type="vertical" />
            <a-popconfirm title="确定删除该分类？" @confirm="handleDelete(record)">
              <a class="danger">删除</a>
            </a-popconfirm>
          </CommonPermissions>
        </template>
      </a-table>
    </a-card>

    <a-modal
      :title="modalTitleText"
      :visible="modalVisible"
      :confirm-loading="modalSubmitting"
      destroy-on-close
      width="520px"
      @ok="handleModalOk"
      @cancel="closeModal"
    >
      <a-form-model ref="formRef" :model="form" :rules="rules" :label-col="{ span: 6 }" :wrapper-col="{ span: 16 }">
        <a-form-model-item v-if="createContext === 'child' && parentHint" label="上级分类">
          <a-input :value="parentHint" disabled />
        </a-form-model-item>
        <a-form-model-item label="分类名称" prop="name">
          <a-input v-model="form.name" placeholder="请输入分类名称" :max-length="100" allow-clear />
        </a-form-model-item>
        <a-form-model-item label="教材类型" prop="bookType">
          <a-select v-model="form.bookType" placeholder="请选择" :disabled="modalMode === 'edit' || createContext === 'child'">
            <a-select-option :value="1">数字教材</a-select-option>
            <a-select-option :value="2">电子书</a-select-option>
          </a-select>
        </a-form-model-item>
        <a-form-model-item v-if="modalMode === 'edit'" label="上级分类">
          <a-input :value="editParentHint" disabled />
        </a-form-model-item>
        <a-form-model-item label="排序" prop="sortKey">
          <a-input-number v-model="form.sortKey" :min="0" placeholder="数值越小越靠前" style="width: 100%" />
        </a-form-model-item>
      </a-form-model>
    </a-modal>
  </div>
</template>

<script>
import CommonPermissions from '@/components/CommonPermissions'
import TextBookApi from '@/api/src/textbook/api/TextBookApi'

const textBookApi = new TextBookApi()

function isRootPid(pid) {
  return pid == null || pid === 0 || pid === ''
}

/** 为每项挂上 parent 引用，并组装成按 sortKey 排序的树；返回根节点数组 */
function buildCategoryTree(flat) {
  if (!flat || !flat.length) return []
  const byId = new Map()
  flat.forEach((raw) => {
    if (raw == null || raw.id == null) return
    byId.set(raw.id, {
      ...raw,
      parent: null,
      children: [],
    })
  })

  const roots = []
  flat.forEach((raw) => {
    if (raw == null || raw.id == null) return
    const node = byId.get(raw.id)
    const pid = raw.pid
    if (isRootPid(pid)) {
      roots.push(node)
      return
    }
    const parent = byId.get(pid)
    if (parent) {
      node.parent = parent
      parent.children.push(node)
    } else {
      node.parent = null
      roots.push(node)
    }
  })

  const sortRec = (nodes) => {
    nodes.sort((a, b) => (a.sortKey ?? 0) - (b.sortKey ?? 0))
    nodes.forEach((n) => {
      if (n.children && n.children.length) sortRec(n.children)
    })
  }
  sortRec(roots)

  const pruneLeaves = (nodes) => {
    nodes.forEach((n) => {
      if (n.children && n.children.length) {
        pruneLeaves(n.children)
      } else {
        delete n.children
      }
    })
  }
  pruneLeaves(roots)

  return roots
}

export default {
  name: 'SettingTextbookCategory',
  components: { CommonPermissions },
  data() {
    return {
      loading: false,
      treeData: [],
      bookTypeFilter: 1,
      columns: [
        // { title: 'ID', dataIndex: 'id', width: 100 },
        { title: '分类名称', dataIndex: 'name', ellipsis: true },
        { title: '教材类型', dataIndex: 'bookType', scopedSlots: { customRender: 'bookType' }, width: 120 },
        { title: '上级分类', key: 'parentName', scopedSlots: { customRender: 'parentName' }, width: 160, ellipsis: true },
        { title: '排序', dataIndex: 'sortKey', width: 88 },
        { title: '操作', key: 'action', scopedSlots: { customRender: 'action' }, width: 260 },
      ],
      modalVisible: false,
      modalSubmitting: false,
      modalMode: 'create',
      /** root: 顶部新建顶级；child: 行内添加子分类 */
      createContext: 'root',
      parentHint: '',
      /** 编辑时只读展示上级；提交时用 editingPid，不可在表单中修改 */
      editParentHint: '',
      editingPid: null,
      editingId: null,
      form: {
        name: '',
        bookType: 1,
        pid: undefined,
        sortKey: 0,
      },
      rules: {
        name: [{ required: true, message: '请输入分类名称', trigger: 'blur' }],
        bookType: [{ required: true, message: '请选择教材类型', trigger: 'change' }],
      },
    }
  },
  computed: {
    modalTitleText() {
      if (this.modalMode === 'edit') return '编辑分类'
      if (this.createContext === 'child') return '新建子分类'
      return '新建分类'
    },
  },
  created() {
    this.fetchList()
  },
  methods: {
    bookTypeLabel(t) {
      const map = { 1: '数字教材', 2: '电子书' }
      return map[t] || '—'
    },
    parentDisplayName(record) {
      if (record.parent && record.parent.name) return record.parent.name
      return ''
    },
    async fetchList() {
      this.loading = true
      try {
        const res = await textBookApi.textBookCategoryList({ bookType: this.bookTypeFilter })
        const flat = Array.isArray(res) ? res : []
        this.treeData = buildCategoryTree(flat)
      } catch (e) {
        console.error(e)
        this.$message.error(e.message || '获取分类列表失败')
        this.treeData = []
      } finally {
        this.loading = false
      }
    },
    /** 顶部：新建第一级分类（无父级） */
    openCreateRoot() {
      this.modalMode = 'create'
      this.createContext = 'root'
      this.parentHint = ''
      this.editParentHint = ''
      this.editingPid = null
      this.editingId = null
      this.form = {
        name: '',
        bookType: this.bookTypeFilter,
        pid: undefined,
        sortKey: 0,
      }
      this.modalVisible = true
      this.$nextTick(() => this.$refs.formRef && this.$refs.formRef.clearValidate())
    },
    /** 行内：在当前行下添加子分类 */
    openAddChild(record) {
      this.modalMode = 'create'
      this.createContext = 'child'
      this.parentHint = (record && record.name) ? `${record.name}（ID: ${record.id}）` : String(record.id)
      this.editParentHint = ''
      this.editingPid = null
      this.editingId = null
      this.form = {
        name: '',
        bookType: record.bookType != null ? record.bookType : this.bookTypeFilter,
        pid: record.id,
        sortKey: 0,
      }
      this.modalVisible = true
      this.$nextTick(() => this.$refs.formRef && this.$refs.formRef.clearValidate())
    },
    openEdit(record) {
      this.modalMode = 'edit'
      this.createContext = 'root'
      this.parentHint = ''
      this.editingId = record.id
      this.editingPid = record.pid
      if (record.parent && record.parent.name) {
        this.editParentHint = `${record.parent.name}（ID: ${record.parent.id}）`
      } else if (isRootPid(record.pid)) {
        this.editParentHint = '无'
      } else {
        this.editParentHint = record.pid != null ? `父级 ID: ${record.pid}` : '—'
      }
      this.form = {
        name: record.name || '',
        bookType: record.bookType != null ? record.bookType : this.bookTypeFilter,
        sortKey: record.sortKey != null ? record.sortKey : 0,
      }
      this.modalVisible = true
      this.$nextTick(() => this.$refs.formRef && this.$refs.formRef.clearValidate())
    },
    closeModal() {
      this.modalVisible = false
    },
    buildPayload() {
      const payload = {
        name: (this.form.name || '').trim(),
        bookType: this.form.bookType,
        sortKey: this.form.sortKey != null ? this.form.sortKey : 0,
      }
      if (this.modalMode === 'edit') {
        const pid = this.editingPid
        if (pid != null && pid !== '') {
          payload.pid = pid
        }
      } else if (this.createContext === 'child') {
        payload.pid = this.form.pid
      }
      return payload
    },
    handleModalOk() {
      this.$refs.formRef.validate(async (valid) => {
        if (!valid) return
        this.modalSubmitting = true
        try {
          const payload = this.buildPayload()
          if (this.modalMode === 'create') {
            await textBookApi.addTextBookCategory(payload)
            this.$message.success('创建成功')
          } else {
            await textBookApi.updateTextBookCategory(this.editingId, payload)
            this.$message.success('保存成功')
          }
          this.modalVisible = false
          await this.fetchList()
        } catch (e) {
          console.error(e)
          this.$message.error(e.message || '操作失败')
        } finally {
          this.modalSubmitting = false
        }
      })
    },
    async handleDelete(record) {
      try {
        await textBookApi.deleteTextBookCategory(record.id)
        this.$message.success('已删除')
        await this.fetchList()
      } catch (e) {
        console.error(e)
        this.$message.error(e.message || '删除失败')
      }
    },
  },
}
</script>

<style scoped lang="less">
.setting-category {
  padding: 0;
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.danger {
  color: #ff4d4f;
}
</style>
